import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getUser } from "store/selector/auth.selector";
import CustomButton from "components/common/Button/CustomButton";
import {
  getPlanFeature,
  getPlanFeatureUpgradeMessage,
  isFamilyAppLoginAllowed,
  isFamilyLoginAllowed,
  isParticipantLoginAllowed,
  isPlanFeatureAllowed,
} from "helpers/planFeatures";

/**
 * Shows upgrade prompt when a plan feature is locked.
 * Use for regions, roles, family_login, in_app_chat entry points.
 */
const PlanUpgradeGate = ({
  feature,
  featureDto = null,
  title = "Upgrade required",
  message = null,
  children = null,
  compact = false,
  user = null,
}) => {
  const navigate = useNavigate();
  const currentUser = useSelector(getUser);
  const billingUser = user ?? currentUser;
  const dto = featureDto ?? getPlanFeature(billingUser, feature);
  const allowed =
    feature === "family_app_access"
      ? isFamilyAppLoginAllowed(billingUser)
      : feature === "family_login"
        ? isFamilyLoginAllowed(billingUser)
        : feature === "participant_login"
          ? isParticipantLoginAllowed(billingUser)
          : isPlanFeatureAllowed(billingUser, feature);

  if (allowed) {
    return children ?? null;
  }

  const body =
    message ??
    getPlanFeatureUpgradeMessage(feature, dto);

  return (
    <div
      className={
        compact
          ? "rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          : "mx-auto max-w-lg rounded-xl border border-[#2f4778]/20 bg-[#eff6ff] p-8 text-center shadow-sm"
      }
    >
      <h2 className={compact ? "font-semibold text-amber-950" : "text-xl font-bold text-[#1e3a5f]"}>
        {title}
      </h2>
      <p className={compact ? "mt-1 text-amber-800" : "mt-3 text-sm text-[#334155]"}>{body}</p>
      {!compact && (
        <div className="mt-6 flex justify-center">
          <CustomButton
            label="View plans"
            clickHandler={() => navigate("/subscription/plans")}
            cssClass="rounded-full px-6"
          />
        </div>
      )}
    </div>
  );
};

export default PlanUpgradeGate;
